/** @jsx jsx */
import { ClassNames, jsx } from "@emotion/core"
import { useManagedConfirmation } from "./useManagedConfirmation"
import { Modal, usePortalNode } from "@cd2/components/build/es6"
import { SerializedStyles } from "@emotion/core"
import ReactDOM from "react-dom"
import { useCallback, useEffect, useRef } from "react"
import { explodePromise } from "../utils/explodePromise"
import { IDialogueConfirmationProps } from "./useDrawConfirmation"

export function useDialogueConfirmation<Res, Data = void>({
  width,
  css: customCss,
  Component,
  outsideResolveValue,
  portal,
}: {
  width?: number
  css?: SerializedStyles
  Component(props: {
    data: Data
    dialogData: Data
    onResolve(response: Res | null): void
  }): JSX.Element
  outsideResolveValue?: Res
  portal?: boolean
}) {
  const portalNode = usePortalNode()
  const [openModal, { showDialog, onResolve, data }] = useManagedConfirmation<Data, Res | null>()

  const CompWithProps = useCallback(
    () => <Component data={data!} dialogData={data!} onResolve={onResolve} />,
    [data],
  )

  let node = (
    <Modal
      css={customCss}
      width={width}
      isOpen={showDialog}
      render={CompWithProps}
      onRequestClose={
        outsideResolveValue !== undefined ? () => onResolve(outsideResolveValue) : undefined
      }
    />
  )

  if (portal) {
    node = ReactDOM.createPortal(node, portalNode.getNode())
  }

  return [openModal, node] as const
}

export function useDialogueConfirmation2<Res, Data = void>({
  width,
  Component,
  componentKey,
  onlyDestroyOnResolve,
  noPadding,
}: {
  width?: number
  Component(props: IDialogueConfirmationProps<Res, Data>): JSX.Element
  componentKey?: string
  // If true, the modal will only be destroyed when the promise is resolved
  // this is needed if you want to remove the underlying component and keep the modal
  // for example if you want to trigger a modal from a tooltip.
  onlyDestroyOnResolve?: boolean
  noPadding?: boolean
}) {
  const outsideClickCbRef = useRef<(() => void) | null>(null)
  function registerOutsideClick(func: () => void) {
    outsideClickCbRef.current = func
  }

  const Node = useCallback(
    ({ showDialog, data, onResolve }) => (
      <ClassNames
        children={({ css }) => (
          <Modal
            width={width}
            onRequestClose={() => {
              outsideClickCbRef.current?.()
            }}
            css={
              noPadding
                ? css`
                    padding: 0 !important;
                  `
                : undefined
            }
            isOpen={showDialog}
            render={() => (
              <div>
                <Component
                  data={data}
                  dialogData={data}
                  onResolve={onResolve}
                  registerOutsideClick={registerOutsideClick}
                />
              </div>
            )}
          />
        )}
      />
    ),
    [componentKey, width],
  )

  const modalRef = useRef<ManagedModal | null>(null)

  useEffect(() => {
    modalRef.current = window.MANAGED_MODALS.create()

    return () => {
      if (!onlyDestroyOnResolve) {
        modalRef.current?.destroy()
      }
    }
  }, [])

  const isOpenRef = useRef(false)
  const openModal = (data: Data) => {
    if (isOpenRef.current) {
      console.warn("Modal is already open, ignoring open request")
      return Promise.resolve(null)
    }
    if (!modalRef.current) {
      console.warn("Modal is not initialized, ignoring open request")
      return Promise.resolve(null)
    }
    isOpenRef.current = true
    const exploded = explodePromise<Res>()

    const onResolve = (response: Res) => exploded.resolve(response)
    // show the modal
    modalRef.current.setComponent(Node, { showDialog: false, data, onResolve })
    setTimeout(() => modalRef.current!.setProps({ showDialog: true, data, onResolve }), 10)
    exploded.promise.then(() => {
      modalRef.current!.setProps({ showDialog: false })
      setTimeout(() => {
        modalRef.current!.setComponent(() => null, {})
        isOpenRef.current = false
      }, 300)
    })

    return exploded.promise
  }

  return openModal
}
